import React, { useState } from 'react';
import {
  Input, Button, Space, Card, Table, Empty, Checkbox,
} from 'antd';
import {
  SearchOutlined, ClearOutlined, PrinterOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWC315Query } from './hooks/usePWC315Query';
import type { ReportItem, CurrencySummary, QueryParams } from '../../services/pwc315Api';
import './PWC315Form.css';

const fl = (text: string) => (
  <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
);

const PWC315Form: React.FC = () => {
  const {
    loading, items, currencySummary, totalNtAmount, totalCount,
    queried, noData, runQuery, reset,
  } = usePWC315Query();

  // ── Filter state ─────────────────────────────────────────────────────────
  const [customerStart, setCustomerStart] = useState('');
  const [customerEnd, setCustomerEnd] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [productStart, setProductStart] = useState('');
  const [productEnd, setProductEnd] = useState('');
  const [deliveryStart, setDeliveryStart] = useState('');
  const [deliveryEnd, setDeliveryEnd] = useState('');
  const [salesStart, setSalesStart] = useState('');
  const [salesEnd, setSalesEnd] = useState('');
  const [sortBy, setSortBy] = useState(1);

  // ── Print type checkboxes (BR-V06: at least one required) ────────────────
  const [printType1, setPrintType1] = useState(false); // 單據別
  const [printType2, setPrintType2] = useState(false); // 客戶別
  const [printType3, setPrintType3] = useState(false); // 日期別
  const [printType4, setPrintType4] = useState(false); // 產品別
  const [printType5, setPrintType5] = useState(false); // 業務員別

  const atLeastOnePrintType = printType1 || printType2 || printType3 || printType4 || printType5;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleQuery = () => {
    const params: QueryParams = {
      customer_start: customerStart.trim() || undefined,
      customer_end: customerEnd.trim() || undefined,
      date_start: dateStart.trim() || undefined,
      date_end: dateEnd.trim() || undefined,
      product_start: productStart.trim() || undefined,
      product_end: productEnd.trim() || undefined,
      delivery_no_start: deliveryStart.trim() || undefined,
      delivery_no_end: deliveryEnd.trim() || undefined,
      sales_start: salesStart.trim() || undefined,
      sales_end: salesEnd.trim() || undefined,
      sort_by: sortBy,
      print_type_1: printType1 || undefined,
      print_type_2: printType2 || undefined,
      print_type_3: printType3 || undefined,
      print_type_4: printType4 || undefined,
      print_type_5: printType5 || undefined,
    };
    runQuery(params);
  };

  const handleReset = () => {
    setCustomerStart(''); setCustomerEnd('');
    setDateStart(''); setDateEnd('');
    setProductStart(''); setProductEnd('');
    setDeliveryStart(''); setDeliveryEnd('');
    setSalesStart(''); setSalesEnd('');
    setSortBy(1);
    setPrintType1(false); setPrintType2(false); setPrintType3(false);
    setPrintType4(false); setPrintType5(false);
    reset();
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!items.length) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const rows = items.map(r =>
      `<tr><td>${r.fc120_no}</td><td>${r.fc120_cust}</td><td>${r.fc120_date}</td>` +
      `<td>${r.fc120_item}</td><td>${r.fc120_curr}</td>` +
      `<td class="right">${r.fc120_qty?.toFixed(2)}</td>` +
      `<td class="right">${r.fc120_amt?.toFixed(2)}</td>` +
      `<td class="right">${r.fc120_nt_amt?.toFixed(0)}</td>` +
      `<td>${r.fc120_sale1 || r.fc120_sale}</td></tr>`
    ).join('');
    win.document.write(
      `<!DOCTYPE html><html><head><title>交運單總表</title>` +
      `<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}` +
      `h2{text-align:center}table{width:100%;border-collapse:collapse}` +
      `th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}` +
      `.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>` +
      `<h2>交運單總表 (pwc315f1)</h2>` +
      `<table><thead><tr><th>交運單號</th><th>客戶代號</th><th>交運日期</th>` +
      `<th>產品代號</th><th>幣別</th><th>數量</th><th>外幣金額</th><th>台幣金額</th><th>業務員</th>` +
      `</tr></thead><tbody>${rows}</tbody></table>` +
      `<p style="margin-top:8px">共 ${totalCount} 筆　台幣合計: ${totalNtAmount?.toFixed(0)}</p>` +
      `</body></html>`
    );
    win.document.close();
    win.focus();
    win.print();
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const itemColumns: ColumnsType<ReportItem> = [
    { title: '交運單號', dataIndex: 'fc120_no', key: 'fc120_no', width: 100, fixed: 'left' as const },
    { title: '客戶代號', dataIndex: 'fc120_cust', key: 'fc120_cust', width: 90 },
    { title: '交運日期', dataIndex: 'fc120_date', key: 'fc120_date', width: 100 },
    { title: '產品代號', dataIndex: 'fc120_item', key: 'fc120_item', width: 120 },
    { title: '色號', dataIndex: 'fc120_col', key: 'fc120_col', width: 60 },
    { title: '單位', dataIndex: 'fc120_unit', key: 'fc120_unit', width: 55 },
    { title: '幣別', dataIndex: 'fc120_curr', key: 'fc120_curr', width: 55 },
    { title: '單價', dataIndex: 'fc120_price', key: 'fc120_price', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(4) },
    { title: '數量', dataIndex: 'fc120_qty', key: 'fc120_qty', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '外幣金額', dataIndex: 'fc120_amt', key: 'fc120_amt', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣金額', dataIndex: 'fc120_nt_amt', key: 'fc120_nt_amt', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '業務代號', dataIndex: 'fc120_sale', key: 'fc120_sale', width: 80 },
    { title: '業務員姓名', dataIndex: 'fc120_sale1', key: 'fc120_sale1', width: 100 },
    { title: '來源', dataIndex: 'source', key: 'source', width: 70 },
  ];

  const currencyColumns: ColumnsType<CurrencySummary> = [
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: '合計金額', dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣合計', dataIndex: 'nt_amount', key: 'nt_amount', align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PermissionGuard module={Modules.PWC315} action="query">
      <div className="pwc315-container">
        <div className="pwc315-header">
          <h2>交運單總表 (pwc315f1)</h2>
          {queried && !noData && items.length > 0 && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          {/* Range filters */}
          <Space wrap size="middle" style={{ marginBottom: 12 }}>
            <div>{fl('客戶代號起')}<Input value={customerStart} onChange={e => setCustomerStart(e.target.value)} placeholder="起" style={{ width: 130 }} allowClear maxLength={12} /></div>
            <div>{fl('客戶代號迄')}<Input value={customerEnd} onChange={e => setCustomerEnd(e.target.value)} placeholder="迄" style={{ width: 130 }} allowClear maxLength={12} /></div>
            <div>{fl('交運日期起')}<Input value={dateStart} onChange={e => setDateStart(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 140 }} allowClear /></div>
            <div>{fl('交運日期迄')}<Input value={dateEnd} onChange={e => setDateEnd(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 140 }} allowClear /></div>
            <div>{fl('產品代號起')}<Input value={productStart} onChange={e => setProductStart(e.target.value)} placeholder="起" style={{ width: 130 }} allowClear maxLength={12} /></div>
            <div>{fl('產品代號迄')}<Input value={productEnd} onChange={e => setProductEnd(e.target.value)} placeholder="迄" style={{ width: 130 }} allowClear maxLength={12} /></div>
            <div>{fl('交運單號起')}<Input value={deliveryStart} onChange={e => setDeliveryStart(e.target.value)} placeholder="起" style={{ width: 110 }} allowClear maxLength={7} /></div>
            <div>{fl('交運單號迄')}<Input value={deliveryEnd} onChange={e => setDeliveryEnd(e.target.value)} placeholder="迄" style={{ width: 110 }} allowClear maxLength={7} /></div>
            <div>{fl('業務代號起')}<Input value={salesStart} onChange={e => setSalesStart(e.target.value)} placeholder="起" style={{ width: 110 }} allowClear maxLength={8} /></div>
            <div>{fl('業務代號迄')}<Input value={salesEnd} onChange={e => setSalesEnd(e.target.value)} placeholder="迄" style={{ width: 110 }} allowClear maxLength={8} /></div>
          </Space>

          {/* Print type checkboxes (BR-V06) */}
          <div className="pwc315-print-types">
            <div className="pwc315-print-label">
              印表選擇（可多重選擇，至少選一）
              {!atLeastOnePrintType && <span className="pwc315-print-warn"> ← 請至少勾選一項</span>}
            </div>
            <Space>
              <Checkbox checked={printType1} onChange={e => setPrintType1(e.target.checked)}>單據別</Checkbox>
              <Checkbox checked={printType2} onChange={e => setPrintType2(e.target.checked)}>客戶別</Checkbox>
              <Checkbox checked={printType3} onChange={e => setPrintType3(e.target.checked)}>日期別</Checkbox>
              <Checkbox checked={printType4} onChange={e => setPrintType4(e.target.checked)}>產品別</Checkbox>
              <Checkbox checked={printType5} onChange={e => setPrintType5(e.target.checked)}>業務員別</Checkbox>
            </Space>
          </div>

          {/* Action buttons */}
          <div style={{ marginTop: 12 }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleQuery}
                loading={loading}
                disabled={!atLeastOnePrintType}
              >
                查詢
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
            </Space>
          </div>
        </Card>

        {/* Results */}
        {queried && noData && <Empty description="查無符合條件的交運單資料" style={{ marginTop: 40 }} />}
        {queried && !noData && items.length > 0 && (
          <>
            <div className="pwc315-result-summary">
              共 <strong>{totalCount}</strong> 筆　台幣合計：<strong>{totalNtAmount?.toFixed(0)}</strong>
            </div>
            <Table<ReportItem>
              columns={itemColumns}
              dataSource={items}
              rowKey={(r, i) => `${r.fc120_no}-${r.fc120_seq}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 100, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1200 }}
              style={{ marginBottom: 16 }}
            />
            {currencySummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>幣別合計</div>
                <Table<CurrencySummary>
                  columns={currencyColumns}
                  dataSource={currencySummary}
                  rowKey="currency"
                  size="small"
                  pagination={false}
                  scroll={{ x: 300 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWC315Form;
